/* Batch #14.150 — PortalImmeubleActionPage : wrapper plein écran pour les tuiles d'action immeuble.
 *
 * Une seule URL `/portal/immeuble/action/:actionId` rend la bonne modale d'action
 * dans une page COMPLÈTE (et non un modal exigu) avec :
 *   - Flèche back « Retour à Gestion immeuble »
 *   - Sélecteur Immeuble + Logement intégré (avec persistance localStorage)
 *   - Le composant action existant rendu DESSOUS
 *
 * URL : /portal/immeuble/action/:actionId
 *   actionId ∈ {safe, stripe, mileage, credit_check, rent_increase, journal, receipts, rl31, rdv}
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "@/lib/toast";
import { ArrowLeft, Building2, Home as HomeIcon, ChevronDown } from "lucide-react";

import { StripeActionModal } from "@/pages/portal/immeuble/modals/StripeActionModal";
import { Rl31ActionModal } from "@/pages/portal/immeuble/modals/Rl31ActionModal";
import { MileageActionModal } from "@/pages/portal/immeuble/modals/MileageActionModal";
import { CreditCheckActionModal } from "@/pages/portal/immeuble/modals/CreditCheckActionModal";
import { RentIncreaseActionModal } from "@/pages/portal/immeuble/modals/RentIncreaseActionModal";
import { JournalActionModal } from "@/pages/portal/immeuble/modals/JournalActionModal";
import { ReceiptsActionModal } from "@/pages/portal/immeuble/modals/ReceiptsActionModal";
import WorkOrdersActionModal from "@/pages/portal/immeuble/modals/WorkOrdersActionModal";
import { mapDashboard, UnifiedDocumentDropModal } from "@/pages/portal/immeuble/ImmeublePortal";
import { API, portalAuthH as authH } from "@/api";


const ACTION_TITLES = {
  safe: "Documents & Coffre-fort",
  stripe: "Prélèvements Stripe",
  rl31: "Relevé 31 (RL-31)",
  mileage: "Kilométrage",
  credit_check: "Enquête de crédit",
  rent_increase: "Renouvellements & Hausses",
  journal: "Conciliation manuelle",
  receipts: "Quittances",
  rdv: "Rendez-vous",
  work_orders: "Suivi des travaux",  // Batch #14.191
};


export default function PortalImmeubleActionPage() {
  const { actionId } = useParams();
  const navigate = useNavigate();

  const [buildings, setBuildings] = useState([]);
  const [apartmentsByBuilding, setApartmentsByBuilding] = useState({});
  const [loading, setLoading] = useState(true);

  // Persisted selection (across navigations) using localStorage
  const [selectedBuildingId, setSelectedBuildingId] = useState(
    () => localStorage.getItem("portal:imm:selectedBuildingId") || null
  );
  const [selectedAptId, setSelectedAptId] = useState(
    () => localStorage.getItem("portal:imm:selectedAptId") || null
  );

  useEffect(() => {
    if (selectedBuildingId) localStorage.setItem("portal:imm:selectedBuildingId", selectedBuildingId);
    else localStorage.removeItem("portal:imm:selectedBuildingId");
  }, [selectedBuildingId]);
  useEffect(() => {
    if (selectedAptId) localStorage.setItem("portal:imm:selectedAptId", selectedAptId);
    else localStorage.removeItem("portal:imm:selectedAptId");
  }, [selectedAptId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/portal/immeuble/dashboard`, { headers: authH() });
      const { buildings: bs, apartmentsByBuilding: abb } = mapDashboard(r.data);
      setBuildings(bs);
      setApartmentsByBuilding(abb);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const apartments = selectedBuildingId ? (apartmentsByBuilding[selectedBuildingId] || []) : [];
  const allApartments = useMemo(() => Object.values(apartmentsByBuilding).flat(), [apartmentsByBuilding]);
  const selectedBuilding = buildings.find((b) => b.id === selectedBuildingId) || null;
  const selectedApt = apartments.find((a) => a.id === selectedAptId) || null;

  const title = ACTION_TITLES[actionId] || "Action de l'immeuble";

  // Batch #14.168 — Retour vers la tuile Immeuble (et non vers l'accueil portail).
  // Le portail rend les tuiles via un state interne (`view`), donc on passe par
  // un query param que PortalDashboard lit au mount.
  const onClose = () => navigate("/portal/dashboard?view=immeuble");
  const onReload = load;

  const renderModal = () => {
    if (loading) {
      return <div className="p-12 text-center text-gray-400">Chargement des immeubles…</div>;
    }
    const common = {
      buildings,
      apartments,
      selectedBuilding,
      selectedApt,
      onBuildingChange: setSelectedBuildingId,
      onAptChange: setSelectedAptId,
      onClose,
      onReload,
    };
    switch (actionId) {
      case "stripe": return <StripeActionModal {...common} />;
      case "safe":
      case "drop": return <UnifiedDocumentDropModal {...common} />;
      case "rl31": return <Rl31ActionModal apartments={apartments} buildings={buildings} onClose={onClose} onReload={onReload} />;
      case "mileage": return <MileageActionModal buildings={buildings} onClose={onClose} onReload={onReload} />;
      case "credit_check": return <CreditCheckActionModal {...common} />;
      case "rent_increase": return <RentIncreaseActionModal apartments={apartments} buildings={buildings} selectedBuilding={selectedBuilding} selectedApt={selectedApt} onClose={onClose} onReload={onReload} />;
      case "journal": return <JournalActionModal apartments={allApartments} buildings={buildings} selectedBuilding={selectedBuilding} onClose={onClose} onReload={onReload} />;
      case "receipts": return <ReceiptsActionModal apartments={apartments} buildings={buildings} onClose={onClose} />;
      // Batch #14.191 — Suivi des travaux
      case "work_orders": return <WorkOrdersActionModal onClose={onClose} />;
      default: return (
        <div className="p-8 text-center text-slate-500">
          <p className="text-sm">Cette action n&apos;est pas encore implémentée en mode pleine page.</p>
          <p className="text-xs mt-2">Action ID : <code className="bg-slate-100 px-2 py-0.5 rounded">{actionId}</code></p>
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100" data-testid={`portal-immeuble-action-${actionId}`}>
      {/* Header sticky */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate("/portal/dashboard?view=immeuble")}
            className="w-10 h-10 rounded-full bg-white border-2 border-gray-200 hover:border-indigo-400 hover:bg-indigo-50 flex items-center justify-center text-gray-600 hover:text-indigo-700 transition shadow-sm"
            data-testid="portal-action-back-btn"
            title="Retour à Gestion immeuble"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-lg font-bold text-gray-900 truncate">{title}</h1>
            <p className="text-xs text-gray-500">Retour à Gestion immeuble</p>
          </div>
        </div>
      </header>

      {/* Selector immeuble + logement (toujours visible) */}
      {!loading && buildings.length > 0 && (
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="portal-action-selectors">
            {/* Immeuble selector */}
            <div className="relative">
              <label className="block text-[10px] uppercase font-semibold text-gray-500 mb-1 flex items-center gap-1">
                <Building2 className="w-3 h-3" /> Immeuble
              </label>
              <div className="relative">
                <select
                  value={selectedBuildingId || ""}
                  onChange={(e) => { setSelectedBuildingId(e.target.value || null); setSelectedAptId(null); }}
                  className="w-full appearance-none px-3 py-2 pr-8 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-indigo-400"
                  data-testid="portal-action-building-select"
                >
                  <option value="">— Choisir un immeuble —</option>
                  {buildings.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.address || b.name || b.id}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
            {/* Logement selector (filtré par immeuble) */}
            <div className="relative">
              <label className="block text-[10px] uppercase font-semibold text-gray-500 mb-1 flex items-center gap-1">
                <HomeIcon className="w-3 h-3" /> Logement
              </label>
              <div className="relative">
                <select
                  value={selectedAptId || ""}
                  onChange={(e) => setSelectedAptId(e.target.value || null)}
                  disabled={!selectedBuildingId}
                  className="w-full appearance-none px-3 py-2 pr-8 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-indigo-400 disabled:bg-gray-50 disabled:text-gray-400"
                  data-testid="portal-action-apt-select"
                >
                  <option value="">{!selectedBuildingId ? "(choisir un immeuble d'abord)" : "— Tous les logements —"}</option>
                  {apartments.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.unit_number || a.label || a.id}{a.tenant_name ? ` — ${a.tenant_name}` : ""}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action body — la modale existante rendue en plein écran (batch #14.164 :
          la classe embedded-modal neutralise le `fixed inset-0 z-50 bg-black/40`
          interne des Action Modals, sinon ils s'affichaient en pop-up FLOTTANT
          par-dessus la page = double fenêtre. */}
      <main className="max-w-7xl mx-auto px-4 py-4 pb-12">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden embedded-modal">
          {renderModal()}
        </div>
      </main>
    </div>
  );
}
